/**
 * The travel-purpose questions at checkout (2026-09-30), presented as coming
 * from the Defera Concierge: what the trip is for, whether the customer is
 * traveling with anyone, and how those companions booked. All required, all
 * un-defaulted (a pre-selected answer is one people skim past, and it records
 * an intent nobody expressed); Review.jsx gates the purchase on them and only
 * renders this card once the passenger details are in. The one exception is
 * the other trip's booking reference (2026-10-08), which is optional.
 *
 * Generic "Defera Concierge" rather than one specialist's name or face: the
 * answers feed the whole desk, not a person the customer has met.
 */
import PropTypes from 'prop-types';
import { ConciergeBell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { COMPANION_REFERENCE_MAX, normalizeCompanionReference } from '@/utils/companionBookingReference';

const optionClass = (active, minH = 'min-h-[52px]') => cn(
  'relative text-left rounded-xl border px-3.5 py-3 transition-all duration-200',
  minH,
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/60',
  active
    ? 'border-brand-600 bg-brand-600/15 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
    : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]',
);

function Dot({ active, className }) {
  return (
    <span className={cn(
      'flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full border transition-colors',
      active ? 'border-brand-300' : 'border-white/30',
      className,
    )}>
      {active && <span className="w-[7px] h-[7px] rounded-full bg-brand-300" />}
    </span>
  );
}
Dot.propTypes = { active: PropTypes.bool, className: PropTypes.string };

const PURPOSES = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'business', label: 'Business' },
  { value: 'event', label: 'Event' },
];
const YES_NO = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];
const COMPANION_BOOKINGS = [
  { value: 'defera', label: 'Another Defera booking', sub: 'We can look for it and seat you together' },
  { value: 'elsewhere', label: 'Booked elsewhere', sub: 'With the airline or another site' },
];

export default function ConciergeTripQuestions({
  purpose,
  onPurposeChange,
  travelingWithOthers,
  onTravelingWithOthersChange,
  companionBooking,
  onCompanionBookingChange,
  companionReference = '',
  onCompanionReferenceChange,
  isMultiPassengerBooking = false,
}) {
  return (
    <div className="mb-8 w-full text-left max-w-2xl mx-auto">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
        <div className="flex flex-col gap-5">

          {/* From the Concierge: who's asking, and why it's worth answering. */}
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-600/20 ring-1 ring-inset ring-brand-600/40">
              <ConciergeBell className="w-4 h-4 text-brand-300" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider font-medium text-brand-300">Defera Concierge</p>
              <h3 className="mt-1 text-[15px] sm:text-base font-medium text-white leading-tight">
                A few quick questions about your trip
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed font-normal text-white/70">
                Your answers help your Concierge tailor your travel experience and look for ways to make this trip even better.
              </p>
            </div>
          </div>

          {/* Purpose */}
          <div>
            <p className="text-[14.5px] font-medium text-white leading-snug">
              What is the purpose of your travel?
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {PURPOSES.map((opt) => {
                const active = purpose === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onPurposeChange(opt.value)}
                    aria-pressed={active}
                    className={optionClass(active)}
                  >
                    <div className="flex items-center gap-2">
                      <Dot active={active} />
                      <span className="text-[13.5px] font-normal text-white">{opt.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Travelling with others — wording depends on party size */}
          <div>
            <p className="text-[14.5px] font-medium text-white leading-snug">
              {isMultiPassengerBooking
                ? 'Will you be traveling with others on this flight aside from passengers on this reservation?'
                : 'Will you be traveling with others on this flight?'}
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {YES_NO.map((opt) => {
                const active = travelingWithOthers === opt.value;
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => onTravelingWithOthersChange(opt.value)}
                    aria-pressed={active}
                    className={optionClass(active)}
                  >
                    <div className="flex items-center gap-2">
                      <Dot active={active} />
                      <span className="text-[13.5px] font-normal text-white">{opt.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Follow-up, only once they've said yes. Indented behind a rule so
                it reads as a consequence of that answer rather than a question
                that was always sitting there. */}
            {travelingWithOthers === true && (
              <div className="mt-4 border-l-2 border-brand-600 pl-3.5">
                <p className="text-[14.5px] font-medium text-white leading-snug">
                  Are they traveling with you through a different booking on Defera, or booked elsewhere?
                </p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {COMPANION_BOOKINGS.map((opt) => {
                    const active = companionBooking === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => onCompanionBookingChange(opt.value)}
                        aria-pressed={active}
                        className={optionClass(active, 'min-h-[58px]')}
                      >
                        <div className="flex items-start gap-2">
                          <Dot active={active} className="mt-0.5" />
                          <span className="min-w-0">
                            <span className="block text-[13.5px] font-normal text-white">{opt.label}</span>
                            <span className="block mt-0.5 text-[11.5px] text-white/45 leading-snug">{opt.sub}</span>
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Optional (2026-10-08): the other booking's reference, so the
                    team can find it without asking. Never required: the other
                    trip may not be booked yet, and then THIS trip's reference
                    goes on that one instead. Staff see exactly what was typed;
                    it links the two bookings only when both are on this
                    account, and staff confirm anyone else's with both
                    travelers first. */}
                {companionBooking === 'defera' && (
                  <div className="mt-4">
                    <label htmlFor="companion-booking-reference" className="block text-[13.5px] font-medium text-white leading-snug">
                      Booking reference of the other trip <span className="font-normal text-white/50">(optional)</span>
                    </label>
                    <input
                      id="companion-booking-reference"
                      type="text"
                      inputMode="text"
                      autoCapitalize="characters"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={COMPANION_REFERENCE_MAX}
                      value={companionReference}
                      onChange={(e) => onCompanionReferenceChange?.(normalizeCompanionReference(e.target.value))}
                      placeholder="e.g. K7Q2XD"
                      aria-describedby="companion-booking-reference-hint"
                      className={cn(
                        'mt-2 w-full sm:max-w-[16rem] rounded-xl border border-white/15 bg-white/[0.03] px-3.5 py-2.5',
                        'font-mono text-base tracking-[0.12em] text-white placeholder:text-white/30 placeholder:tracking-normal',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/60',
                      )}
                    />
                    <p id="companion-booking-reference-hint" className="mt-1.5 text-[11.5px] font-normal text-white/45 leading-snug">
                      It&apos;s on that trip&apos;s confirmation email and in Trips. Booking the other trip after this one? Leave this blank, and enter this trip&apos;s reference when you book it.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

ConciergeTripQuestions.propTypes = {
  purpose: PropTypes.oneOf(['vacation', 'business', 'event', null]),
  onPurposeChange: PropTypes.func.isRequired,
  travelingWithOthers: PropTypes.bool,
  onTravelingWithOthersChange: PropTypes.func.isRequired,
  companionBooking: PropTypes.oneOf(['defera', 'elsewhere', null]),
  onCompanionBookingChange: PropTypes.func.isRequired,
  companionReference: PropTypes.string,
  onCompanionReferenceChange: PropTypes.func,
  isMultiPassengerBooking: PropTypes.bool,
};
